import React, { useState, useEffect } from "react";
import { useConfig } from "../../../../../../app/configcontext";
import { useSelector, useDispatch } from "react-redux";
import { confirmOtp, createBusiness, resendOtp } from "../../../../../../services/MasterApi";
import { Link, useNavigate } from "react-router-dom";
import { getData } from "./calculateTotalPrice";
import { Modal, Input, Spin, Button, Tooltip, message } from "antd";
import { FaInfoCircle } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import ProtectedPaymentRoute from "../../../../../auth/Auth";
import { OKTOBIZ_LOGO } from "./logoBase64";
import { update_total_amount } from "../../../../../../features/business/businessSlice";

export default function TotalPrice() {
  ProtectedPaymentRoute();

  const dispatch = useDispatch();
  const config = useConfig();
  const navigate = useNavigate();
  const business = useSelector((state) => state.business);
  const username = business.primaryContact?.email;

  const [businessError, setBusinessError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [isConfirmingOtp, setIsConfirmingOtp] = useState(false);
  const data = getData(business, config);
  const prices = data.map((item) => item.price);
  
  const stateName = business?.businessLocation?.state?.toLowerCase() || '';
  let filingFees = 100;
  let stateFilingFee = 100;
  let stateExpediteFee = 0;
  let priorityFee = 0;

  if (stateName === 'delaware') { stateFilingFee = 109; stateExpediteFee = 50; priorityFee = 30; filingFees = 189; }
  else if (stateName === 'maryland') { stateFilingFee = 100; stateExpediteFee = 50; priorityFee = 30; filingFees = 180; }
  else if (stateName === 'wyoming') { stateFilingFee = 100; stateExpediteFee = 0; priorityFee = 30; filingFees = 130; }
  else if (stateName === 'michigan') { stateFilingFee = 50; stateExpediteFee = 50; priorityFee = 30; filingFees = 130; }

  const subtotal = Object.values(prices).reduce((acc, price) => acc + price, 0);
  const total = subtotal + filingFees;
  const businessName = business?.businessDetails?.businessName || "Your Business";
  const entityType = business.businessTypeId === 2 ? "Corporation" : "Limited Liability Company (LLC)";
  
  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(Number(value) || 0);

  const [userId, setUserId] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);

  useEffect(() => {
    const access = localStorage.getItem("accessForInvoice");
    if (!access) {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    if (subtotal !== undefined && filingFees !== undefined) {
      dispatch(
        update_total_amount({
          subTotal: Number(subtotal) || 0,
          salesTax: 0,
          filingFee: Number(filingFees) || 0,
          total: Number(total) || 0,
        })
      );
    }
  }, [dispatch, subtotal, filingFees, total]);

  // ── Google Enterprise Styled PDF Invoice Generator ────────────────────────
  const generatePdfDoc = () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const textColorDark = [32, 33, 36];     // #202124 (Google Dark)
    const textColorMuted = [95, 99, 104];   // #5f6368 (Google Grey)
    const googleBlue = [26, 115, 232];      // #1a73e8 (Google Blue)
    const borderColor = [218, 220, 224];    // #dadce0 (Google Border)
    const lightRowBg = [248, 249, 250];     // #f8f9fa (Google Light)

    // 1. Logo / Header Section (Embed exact official Oktobiz navbar logo)
    try {
      doc.addImage(OKTOBIZ_LOGO, "PNG", 15, 12, 42, 10);
    } catch (e) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(googleBlue[0], googleBlue[1], googleBlue[2]);
      doc.text("OKTOBIZ", 15, 22);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(textColorMuted[0], textColorMuted[1], textColorMuted[2]);
    doc.text("Business Formation & Legal Compliance Services", 15, 28);
    doc.text("support@oktobiz.com  ·  https://oktobiz.com", 15, 32.5);

    // 2. Top Right: Invoice Title & Aligned Metadata
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(textColorDark[0], textColorDark[1], textColorDark[2]);
    doc.text("INVOICE", 195, 18, { align: "right" });

    const orderId = `OKT-${Math.floor(100000 + Math.random() * 900000)}`;
    const dateStr = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(textColorMuted[0], textColorMuted[1], textColorMuted[2]);
    doc.text("Invoice Number:", 125, 26);
    doc.setTextColor(textColorDark[0], textColorDark[1], textColorDark[2]);
    doc.text(`${orderId}`, 195, 26, { align: "right" });

    doc.setTextColor(textColorMuted[0], textColorMuted[1], textColorMuted[2]);
    doc.text("Invoice Date:", 125, 31);
    doc.setTextColor(textColorDark[0], textColorDark[1], textColorDark[2]);
    doc.text(`${dateStr}`, 195, 31, { align: "right" });

    doc.setTextColor(textColorMuted[0], textColorMuted[1], textColorMuted[2]);
    doc.text("Payment Status:", 125, 36);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(googleBlue[0], googleBlue[1], googleBlue[2]);
    doc.text("Pending Payment", 195, 36, { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.setTextColor(textColorMuted[0], textColorMuted[1], textColorMuted[2]);
    doc.text("Payment Terms:", 125, 41);
    doc.setTextColor(textColorDark[0], textColorDark[1], textColorDark[2]);
    doc.text("Due Upon Receipt", 195, 41, { align: "right" });

    // Clean Flat Divider Line
    doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
    doc.setLineWidth(0.4);
    doc.line(15, 46, 195, 46);

    // 3. Customer & Entity Details (2-Column Google Layout)
    const infoY = 54;

    // Left Column: Customer Information
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(textColorMuted[0], textColorMuted[1], textColorMuted[2]);
    doc.text("BILLED TO", 15, infoY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(textColorDark[0], textColorDark[1], textColorDark[2]);
    doc.text(`${business.primaryContact?.fullName || "Valued Customer"}`, 15, infoY + 5.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(textColorMuted[0], textColorMuted[1], textColorMuted[2]);
    doc.text(`Email: ${business.primaryContact?.email || "--"}`, 15, infoY + 10.5);
    doc.text(`Customer ID: #${orderId}`, 15, infoY + 15);
    const membersCount = business.businessMembers?.length || 1;
    doc.text(`Authorized Members: ${membersCount} Officer(s)`, 15, infoY + 19.5);

    // Right Column: Entity Formation Information
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(textColorMuted[0], textColorMuted[1], textColorMuted[2]);
    doc.text("FORMING ENTITY DETAILS", 110, infoY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(textColorDark[0], textColorDark[1], textColorDark[2]);
    doc.text(`${business.businessDetails?.businessName || "--"}`, 110, infoY + 5.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(textColorMuted[0], textColorMuted[1], textColorMuted[2]);
    doc.text(`Entity Structure: ${entityType}`, 110, infoY + 10.5);
    doc.text(`Jurisdiction: State of ${business.businessLocation?.state || "--"}`, 110, infoY + 15);
    const addressStr = `${business.businessLocation?.street || ""}, ${business.businessLocation?.city || ""} ${business.businessLocation?.zipcode || ""}`;
    doc.text(`Principal Address: ${addressStr.length > 34 ? addressStr.substring(0, 34) + "..." : addressStr}`, 110, infoY + 19.5);

    // 4. Clean Itemized Services Table (Only selected items in body; state fee in subtotal calculation)
    const tableData = data.map((item, idx) => [
      String(idx + 1).padStart(2, "0"),
      item.name,
      "Formation & Legal Service",
      "1",
      formatCurrency(item.price),
    ]);

    autoTable(doc, {
      startY: 82,
      head: [["#", "Service Description", "Category", "Qty", "Amount"]],
      body: tableData,
      foot: [
        [
          { content: "Subtotal", colSpan: 4, styles: { halign: "right", fontStyle: "normal", textColor: textColorMuted } },
          { content: formatCurrency(subtotal), styles: { halign: "right", fontStyle: "normal", textColor: textColorDark } }
        ],
        [
          { content: `${business.businessLocation?.state || "State"} Statutory Government Filing Fee`, colSpan: 4, styles: { halign: "right", fontStyle: "normal", textColor: textColorMuted } },
          { content: formatCurrency(filingFees), styles: { halign: "right", fontStyle: "normal", textColor: textColorDark } }
        ],
        [
          { content: "Total Due (USD)", colSpan: 4, styles: { halign: "right", fontStyle: "bold", fontSize: 9.5, textColor: textColorDark, fillColor: [248, 249, 250] } },
          { content: formatCurrency(total), styles: { halign: "right", fontStyle: "bold", fontSize: 11, textColor: googleBlue, fillColor: [248, 249, 250] } }
        ]
      ],
      theme: "grid",
      styles: {
        lineColor: borderColor,
        lineWidth: 0.2,
        fontSize: 8.5,
        textColor: textColorDark,
        cellPadding: { top: 3.8, bottom: 3.8, left: 3.5, right: 3.5 },
      },
      headStyles: {
        fillColor: [248, 249, 250], // #f8f9fa clean Google table header
        textColor: textColorDark,
        fontStyle: "bold",
        fontSize: 8.5,
        lineColor: borderColor,
        lineWidth: 0.3,
      },
      footStyles: {
        lineColor: borderColor,
        lineWidth: 0.2,
        fillColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 85 },
        2: { cellWidth: 45, textColor: textColorMuted },
        3: { cellWidth: 15, halign: "center", textColor: textColorMuted },
        4: { cellWidth: 25, halign: "right", fontStyle: "bold", textColor: textColorDark },
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      margin: { left: 15, right: 15 },
    });

    // 5. Service Guarantee & Statutory Protection (Full-Width Clean Flat Box with Blue Accent)
    const finalY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : 165) + 8;
    const boxWidth = 180;
    const boxHeight = 24;

    doc.setFillColor(248, 249, 250);
    doc.rect(15, finalY, boxWidth, boxHeight, "F");
    doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
    doc.setLineWidth(0.2);
    doc.rect(15, finalY, boxWidth, boxHeight, "S");

    // Blue Left Border Accent (Flat Google Style)
    doc.setFillColor(googleBlue[0], googleBlue[1], googleBlue[2]);
    doc.rect(15, finalY, 1.5, boxHeight, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(textColorDark[0], textColorDark[1], textColorDark[2]);
    doc.text("Service Guarantee & Statutory Filing Protection", 20, finalY + 5.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.8);
    doc.setTextColor(textColorMuted[0], textColorMuted[1], textColorMuted[2]);
    doc.text("- 100% State Approval & Accuracy Guarantee on all articles of organization filed with the Secretary of State.", 20, finalY + 11);
    doc.text("- Secure encrypted cloud vault storage with digital certified document access and compliance notifications.", 20, finalY + 15.5);
    doc.text("- Dedicated customer support and ongoing state franchise tax & compliance alerts are included.", 20, finalY + 20);

    // 6. Flat Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
    doc.setLineWidth(0.3);
    doc.line(15, pageHeight - 16, 195, pageHeight - 16);

    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(textColorMuted[0], textColorMuted[1], textColorMuted[2]);
    doc.text("Questions? Contact our corporate team at support@oktobiz.com or visit https://oktobiz.com.", 15, pageHeight - 10);
    doc.text("Page 1 of 1  ·  Official Formation Order Invoice", 195, pageHeight - 10, { align: "right" });

    return doc;
  };

  const handleDownloadPDF = () => {
    try {
      setIsDownloadingPdf(true);
      const doc = generatePdfDoc();
      const filename = "Oktobiz_Invoice.pdf";
      const blob = doc.output("blob");

      // Wrap in File object to preserve exact filename & PDF MIME across all browsers
      const file = new File([blob], filename, { type: "application/pdf" });
      const url = window.URL.createObjectURL(file);

      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      link.download = filename;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 2000);

      setIsDownloadingPdf(false);
      message.success("Invoice PDF downloaded successfully!");
    } catch (err) {
      console.error("PDF generation error:", err);
      setIsDownloadingPdf(false);
      message.error("Failed to generate PDF. Please try again.");
    }
  };

  const handlePreviewPDF = () => {
    try {
      setIsDownloadingPdf(true);
      const doc = generatePdfDoc();
      const filename = "Oktobiz_Invoice.pdf";
      const blob = doc.output("blob");
      const file = new File([blob], filename, { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(file);

      const win = window.open(blobUrl, "_blank");
      if (win) {
        win.document.title = filename;
      }
      setIsDownloadingPdf(false);
    } catch (err) {
      console.error("Preview error:", err);
      setIsDownloadingPdf(false);
      handleDownloadPDF();
    }
  };

  const postData = () => {
    // If order was already created in this session, simply reopen the OTP verification modal
    const existingTransactionId = localStorage.getItem("transactionId");
    if (existingTransactionId && isOtpSent) {
      setShowOtpModal(true);
      return;
    }

    setIsLoading(true);
    setBusinessError("");

    // Strip redux-persist internal field and inject exact calculated amounts
    const { _persist, ...businessData } = business;
    const dynamicTotal = {
      subTotal: Number(subtotal) || 0,
      salesTax: 0,
      filingFee: Number(filingFees) || 0,
      total: Number(total) || 0,
    };

    dispatch(update_total_amount(dynamicTotal));

    const businessPayload = {
      ...businessData,
      totalAmount: dynamicTotal,
    };

    createBusiness(businessPayload)
      .then((response) => {
        if (response?.data?.content) {
          setIsOtpSent(true);
          setShowOtpModal(true);
          setUserId(response.data.content.userId);
          setTransactionId(response.data.content.businessTransactionId);
          localStorage.setItem("userToken", response.data.content.userToken);
          localStorage.setItem("transactionId", response.data.content.businessTransactionId);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setBusinessError(error.response?.data?.message || "An error occurred while creating your order.");
        setIsLoading(false);
      });
  };

  const handleRequestCloseOtp = () => {
    setShowCancelWarning(true);
  };

  const handleConfirmCancelOtp = () => {
    setShowCancelWarning(false);
    setShowOtpModal(false);
    message.info("Email verification paused. Click 'Verify & Proceed' whenever you're ready.");
  };

  const handleResumeOtp = () => {
    setShowCancelWarning(false);
    setShowOtpModal(true);
  };

  const handleResendOTP = () => {
    if (!username) {
      setOtpError("Primary contact email is missing.");
      return;
    }
    setIsLoading(true);
    setOtpError("");
    resendOtp(username)
      .then(() => {
        setIsLoading(false);
        message.success("A fresh OTP code has been sent to your email!");
      })
      .catch((error) => {
        setOtpError(error.response?.data?.message || "Error resending OTP. Please try again.");
        setIsLoading(false);
      });
  };

  const handleOtpConfirmation = () => {
    if (!otp.trim()) {
      setOtpError("Please enter the 6-digit OTP code");
      return;
    }

    setIsConfirmingOtp(true);
    setOtpError("");

    confirmOtp(username, otp.trim())
      .then(() => {
        setIsConfirmingOtp(false);
        setShowOtpModal(false);
        navigate("/payment");
      })
      .catch((error) => {
        setOtpError(error.response?.data?.message || "Invalid OTP code. Please try again.");
        setIsConfirmingOtp(false);
      });
  };

  const backBtn = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-[80vh] bg-[#f8f9fa] py-8 lg:py-14 font-sans">
      <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
        
        {/* Navigation & Breadcrumb */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={backBtn}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-[4px] border border-[#dadce0] bg-white text-[#1a73e8] text-[13.5px] font-medium hover:bg-[#f1f3f4] transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-arrow-left text-xs"></i> Back
          </button>
          <span className="text-xs font-medium text-[#5f6368] tracking-wide uppercase">
            Step 8 of 8: Order Confirmation
          </span>
        </div>

        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-normal text-[#202124] tracking-tight">
            Review your formation details
          </h1>
          <p className="text-[14px] text-[#5f6368] mt-1">
            Please verify all details for <span className="text-[#1a73e8] font-medium">{businessName}</span> before proceeding to payment.
          </p>
        </div>

        {/* ── Main 2-Column Split Layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* ── Left Column: Formed Entity Review Cards (7 Cols) ── */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* Card 1: Business Information */}
            <div className="bg-white rounded-[12px] border border-[#dadce0] p-6 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-[#f1f3f4]">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#e8f0fe] text-[#1a73e8] flex items-center justify-center text-sm">
                    <i className="fa-solid fa-building"></i>
                  </div>
                  <h2 className="text-[16px] font-medium text-[#202124]">Company Details</h2>
                </div>
                <Link
                  to={{ pathname: "/business_name", search: "?edit=true" }}
                  className="px-3 py-1 text-xs font-medium text-[#1a73e8] bg-[#f8fafd] border border-[#dadce0] rounded hover:bg-[#e8f0fe] transition-colors"
                >
                  Edit Name
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[13.5px]">
                <div>
                  <span className="block text-xs font-medium text-[#5f6368] mb-0.5">Legal Business Name</span>
                  <span className="text-[#202124] font-medium">{businessName}</span>
                </div>
                <div>
                  <span className="block text-xs font-medium text-[#5f6368] mb-0.5">Entity Structure</span>
                  <span className="text-[#202124]">{entityType}</span>
                </div>
                <div>
                  <span className="block text-xs font-medium text-[#5f6368] mb-0.5">Formation State</span>
                  <span className="text-[#202124]">{business?.businessLocation?.state || "--"}</span>
                </div>
                <div>
                  <span className="block text-xs font-medium text-[#5f6368] mb-0.5">Industry Type</span>
                  <span className="text-[#202124]">{business?.businessType?.name || "General Business"}</span>
                </div>
              </div>
            </div>

            {/* Card 2: Registered Business Location */}
            <div className="bg-white rounded-[12px] border border-[#dadce0] p-6 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-[#f1f3f4]">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#e8f0fe] text-[#1a73e8] flex items-center justify-center text-sm">
                    <i className="fa-solid fa-location-dot"></i>
                  </div>
                  <h2 className="text-[16px] font-medium text-[#202124]">Principal Address</h2>
                </div>
                <Link
                  to={{ pathname: "/business_location", search: "?edit=true" }}
                  className="px-3 py-1 text-xs font-medium text-[#1a73e8] bg-[#f8fafd] border border-[#dadce0] rounded hover:bg-[#e8f0fe] transition-colors"
                >
                  Edit Address
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[13.5px]">
                <div className="sm:col-span-2">
                  <span className="block text-xs font-medium text-[#5f6368] mb-0.5">Street Address</span>
                  <span className="text-[#202124]">{business?.businessLocation?.street || "--"}</span>
                </div>
                <div>
                  <span className="block text-xs font-medium text-[#5f6368] mb-0.5">City & State</span>
                  <span className="text-[#202124]">
                    {business?.businessLocation?.city || "--"}, {business?.businessLocation?.state || "--"}
                  </span>
                </div>
                <div>
                  <span className="block text-xs font-medium text-[#5f6368] mb-0.5">ZIP Code</span>
                  <span className="text-[#202124]">{business?.businessLocation?.zipcode || "--"}</span>
                </div>
              </div>
            </div>

            {/* Card 3: Primary Contact & Signer */}
            <div className="bg-white rounded-[12px] border border-[#dadce0] p-6 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-[#f1f3f4]">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#e8f0fe] text-[#1a73e8] flex items-center justify-center text-sm">
                    <i className="fa-solid fa-user-check"></i>
                  </div>
                  <h2 className="text-[16px] font-medium text-[#202124]">Primary Contact & Signer</h2>
                </div>
                <Link
                  to={{ pathname: "/contact_details", search: "?edit=true" }}
                  className="px-3 py-1 text-xs font-medium text-[#1a73e8] bg-[#f8fafd] border border-[#dadce0] rounded hover:bg-[#e8f0fe] transition-colors"
                >
                  Edit Contact
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[13.5px]">
                <div>
                  <span className="block text-xs font-medium text-[#5f6368] mb-0.5">Contact Name</span>
                  <span className="text-[#202124]">{business?.primaryContact?.fullName || "--"}</span>
                </div>
                <div>
                  <span className="block text-xs font-medium text-[#5f6368] mb-0.5">Email Address</span>
                  <span className="text-[#202124] break-all">{business?.primaryContact?.email || "--"}</span>
                </div>
              </div>
            </div>

            {/* Card 4: Business Members / Officers */}
            <div className="bg-white rounded-[12px] border border-[#dadce0] p-6 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-[#f1f3f4]">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#e8f0fe] text-[#1a73e8] flex items-center justify-center text-sm">
                    <i className="fa-solid fa-users"></i>
                  </div>
                  <h2 className="text-[16px] font-medium text-[#202124]">Authorized Officers & Members</h2>
                </div>
                <Link
                  to={{ pathname: business.businessTypeId === 1 ? "/member" : "/member_corpo", search: "?edit=true" }}
                  className="px-3 py-1 text-xs font-medium text-[#1a73e8] bg-[#f8fafd] border border-[#dadce0] rounded hover:bg-[#e8f0fe] transition-colors"
                >
                  Edit Members
                </Link>
              </div>

              <div className="space-y-3">
                {(business.businessMembers || []).map((ele, index) => (
                  <div
                    key={`${ele.firstName || "member"}-${index}`}
                    className="p-3 rounded-[8px] bg-[#f8f9fa] border border-[#dadce0] flex items-center justify-between text-[13.5px]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#1a73e8] text-white flex items-center justify-center text-xs font-semibold">
                        {(ele.firstName || "M").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span className="font-medium text-[#202124] block">{ele.firstName} {ele.lastName}</span>
                        {ele.email && <span className="text-xs text-[#5f6368]">{ele.email}</span>}
                      </div>
                    </div>
                    {ele.ownership && (
                      <span className="px-2.5 py-1 rounded bg-[#e8f0fe] text-[#1a73e8] text-xs font-medium">
                        {ele.ownership}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* ── Right Column: Sticky Order Summary & Checkout (5 Cols) ── */}
          <div className="lg:col-span-5 space-y-5 lg:sticky lg:top-20">
            
            {/* Order Summary Card */}
            <div className="bg-white rounded-[16px] border border-[#dadce0] shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-[#f8fafd] border-b border-[#dadce0] flex items-center justify-between">
                <h3 className="text-[16px] font-medium text-[#202124]">Order Summary</h3>
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#e6f4ea] text-[#1e8e3e]">
                  Active Plan
                </span>
              </div>

              {/* Line Items List */}
              <div className="p-6 divide-y divide-[#f1f3f4] space-y-3">
                
                {data.map((ele) => (
                  <div key={ele.name} className="flex items-center justify-between text-[14px] pt-3 first:pt-0">
                    <span className="text-[#3c4043]">{ele.name}</span>
                    <span className="text-[#202124] font-medium">{formatCurrency(ele.price)}</span>
                  </div>
                ))}

                {/* Subtotal */}
                <div className="flex items-center justify-between text-[13.5px] pt-3 text-[#5f6368]">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>

                {/* State Filing Fee */}
                <div className="flex items-center justify-between text-[13.5px] pt-3 text-[#5f6368]">
                  <div className="flex items-center gap-1.5">
                    <span>{business.businessLocation?.state || "State"} Mandatory Filing Fee</span>
                    <Tooltip 
                      title={
                        <div className="p-1 space-y-1 text-xs">
                          <div className="flex justify-between gap-4"><span>State Filing Fee:</span> <span>{formatCurrency(stateFilingFee)}</span></div>
                          {stateExpediteFee > 0 && <div className="flex justify-between gap-4"><span>State Expedite Fee:</span> <span>{formatCurrency(stateExpediteFee)}</span></div>}
                          {priorityFee > 0 && <div className="flex justify-between gap-4"><span>Priority Fee:</span> <span>{formatCurrency(priorityFee)}</span></div>}
                        </div>
                      }
                      placement="left"
                    >
                      <FaInfoCircle className="text-[#9aa0a6] hover:text-[#1a73e8] cursor-pointer text-xs" />
                    </Tooltip>
                  </div>
                  <span>{formatCurrency(filingFees)}</span>
                </div>

              </div>

              {/* Total Due Row */}
              <div className="px-6 py-5 bg-[#f8fafd] border-t border-[#dadce0]">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[15px] font-medium text-[#202124]">Total Due Today</span>
                  <span className="text-2xl font-normal text-[#1a73e8] tracking-tight">
                    {formatCurrency(total)}
                  </span>
                </div>
                <span className="text-[11.5px] text-[#5f6368] block">
                  Includes state statutory filing fees and all selected packages
                </span>
              </div>

              {/* Action Buttons */}
              <div className="p-6 pt-2 space-y-3">
                {isOtpSent && !showOtpModal && (
                  <div className="p-3 rounded-[8px] bg-[#e8f0fe] border border-[#1a73e8]/30 flex items-start gap-2.5 text-[13px]">
                    <i className="fa-solid fa-envelope-circle-check text-[#1a73e8] mt-0.5 text-base flex-shrink-0"></i>
                    <div>
                      <span className="font-medium block text-[#185abc]">OTP Verification Pending</span>
                      <span className="text-[#3c4043] text-xs">
                        A verification code was sent to {business.primaryContact?.email}. Click below to enter code.
                      </span>
                    </div>
                  </div>
                )}

                {businessError && (
                  <div className="p-3 rounded-[6px] bg-[#fce8e6] border border-[#d93025] text-[#d93025] text-[13px] flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                    <span>{businessError}</span>
                  </div>
                )}

                <button
                  onClick={postData}
                  disabled={isLoading || showOtpModal}
                  className="w-full py-3.5 rounded-[4px] bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium shadow-sm transition-all text-[15px] flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <Spin size="small" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>{isOtpSent ? "Verify Email & Proceed to Payment" : "Continue to Checkout"}</span>
                      <i className="fa-solid fa-arrow-right text-xs"></i>
                    </>
                  )}
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={handleDownloadPDF}
                    disabled={isDownloadingPdf}
                    className="py-2.5 px-2 rounded-[4px] border border-[#dadce0] bg-white text-[#1a73e8] hover:bg-[#f8f9fa] font-medium transition-colors text-[13px] cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    {isDownloadingPdf ? (
                      <>
                        <Spin size="small" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-download text-xs"></i>
                        <span>Download PDF</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handlePreviewPDF}
                    className="py-2.5 px-2 rounded-[4px] border border-[#dadce0] bg-white text-[#5f6368] hover:text-[#202124] hover:bg-[#f8f9fa] font-medium transition-colors text-[13px] cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square text-xs"></i>
                    <span>Preview & Print</span>
                  </button>
                </div>
              </div>

            </div>

            {/* Trust Badges Card */}
            <div className="p-4 rounded-[12px] border border-[#dadce0] bg-white text-center space-y-2">
              <div className="flex items-center justify-center gap-6 text-xs text-[#5f6368]">
                <span className="flex items-center gap-1.5">
                  <i className="fa-solid fa-shield-halved text-[#1e8e3e]"></i> 256-Bit SSL Encrypted
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="fa-solid fa-circle-check text-[#1a73e8]"></i> 100% State Guarantee
                </span>
              </div>
            </div>

          </div>

        </div>

        {/* OTP Verification Modal */}
        <Modal
          open={showOtpModal}
          onCancel={handleRequestCloseOtp}
          footer={null}
          centered
          title={
            <div className="text-center pb-2">
              <h3 className="text-xl font-normal text-[#202124]">Verify Your Email</h3>
              <p className="text-xs text-[#5f6368] mt-1 font-normal">
                We sent a verification code to <span className="text-[#1a73e8] font-medium">{business.primaryContact?.email}</span>
              </p>
            </div>
          }
        >
          <div className="py-4 space-y-4">
            <div>
              <label className="block text-[13px] font-medium text-[#3c4043] mb-1.5">
                Verification OTP Code <span className="text-[#d93025]">*</span>
              </label>
              <Input
                autoFocus
                placeholder="Enter 6-digit OTP"
                size="large"
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value);
                  setOtpError("");
                }}
                status={otpError ? "error" : ""}
                className="w-full text-center tracking-widest text-lg font-semibold"
              />
              {otpError && (
                <p className="text-[#d93025] text-xs mt-1.5 font-medium">{otpError}</p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isLoading}
                className="text-xs text-[#1a73e8] hover:underline cursor-pointer bg-transparent border-none"
              >
                {isLoading ? "Sending..." : "Resend OTP Code"}
              </button>
            </div>

            <Button
              type="primary"
              onClick={handleOtpConfirmation}
              loading={isConfirmingOtp}
              className="w-full h-11 bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium rounded-[4px] mt-2 shadow-sm text-[15px]"
            >
              Confirm OTP & Proceed to Payment
            </Button>
          </div>
        </Modal>

        {/* Cancellation Warning Confirmation Modal */}
        <Modal
          open={showCancelWarning}
          onCancel={() => setShowCancelWarning(false)}
          footer={null}
          centered
          width={440}
          closable={false}
        >
          <div className="p-2 text-center">
            <div className="w-12 h-12 rounded-full bg-[#fef7e0] text-[#b45a00] flex items-center justify-center mx-auto mb-3.5 text-xl">
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h3 className="text-lg font-medium text-[#202124] mb-2">
              Pause Email Verification?
            </h3>
            <p className="text-[13.5px] text-[#5f6368] leading-relaxed mb-6">
              Email verification is required to proceed with payment and register your company. Your details are safely saved, and you can resume anytime by clicking <span className="font-medium text-[#202124]">Verify & Proceed</span>.
            </p>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleConfirmCancelOtp}
                className="flex-1 py-2.5 rounded-[4px] border border-[#dadce0] text-[#5f6368] hover:text-[#202124] hover:bg-[#f8f9fa] font-medium transition-colors text-[13.5px] cursor-pointer"
              >
                Exit for Now
              </button>
              <button
                type="button"
                onClick={handleResumeOtp}
                className="flex-1 py-2.5 rounded-[4px] bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium shadow-sm transition-all text-[13.5px] cursor-pointer"
              >
                Keep Verifying
              </button>
            </div>
          </div>
        </Modal>

      </div>
    </div>
  );
}
